// 리포트 원본 데이터(DB row 형태, GET /report/:id/data 응답)와 집계 로직.
// 더미(Sample 탭)와 실데이터(Detail 탭)가 모두 이 모듈을 거친다.

export const OK_LABEL = "OK";

export interface ReportLabelRow {
  id: number;
  path: string; // projectImage.path
  label: string; // 사용자 라벨(정답), "" = 미라벨
  AILabel: string; // AI 판정
  score: number;
  recentLabel: string;
  ai_update_at: string | null;
}

export interface ReportTrainRow {
  id: number;
  iteration: number;
  class_name: string; // 분석: JSON 배열 문자열 '["dent","bubble"]'
  epoch: number;
  imageCount: number;
  accuracy: number | null;
  status: "Pending" | "Training" | "Finished" | "Failed" | "Stop";
  create_at: string;
  finish_at: string | null;
  trainLog_uuid: string | null;
}

export interface ReportInspModelRow {
  id: number;
  name: string;
  create_at: string;
  trainLog_uuid: string | null;
  info: string | null; // JSON: { performance: { overkill, missing, threshold }, epoch: {...}, ... }
}

export interface ReportValidHistoryMeta {
  id: number;
  finished_at: string;
  model_uuid: string | null;
  trainHistory_id: number | null;
}

export interface ReportValidation extends ReportValidHistoryMeta {
  results: string; // JSON: { items: [{ path, label, score }], threshold }
  model: { uuid: string; name: string; create_at: string; info: string | null } | null; // inspModel 또는 epochTrainModel
}

export interface ReportRawData {
  project: {
    id: number;
    project_name: string;
    step: string;
    class: string; // JSON 배열 문자열
    material_name: string;
    create_at: string;
  };
  labels: ReportLabelRow[];
  trainHistory: ReportTrainRow[];
  inspModels: ReportInspModelRow[];
  validHistories?: ReportValidHistoryMeta[]; // state=end, 최신순
  validation?: ReportValidation | null; // 리포트에 사용된 검증 결과
}

export interface ClassStat {
  className: string;
  labeled: number; // 사용자 라벨 수
  predicted: number; // AI 판정 수
  correct: number;
  recall: number | null; // 정답 중 AI가 맞힌 비율 (= 클래스 정확도)
  precision: number | null;
  overkill: number; // 정답 OK 인데 이 클래스로 판정
  missing: number; // 정답이 이 클래스인데 OK 로 판정
}

export interface ProcessStat {
  process: string;
  total: number;
  labeled: number;
  byClass: Record<string, number>; // 사용자 라벨 기준 NG 클래스별 수
  mismatch: number; // 라벨 ≠ AI
  mismatchRate: number | null;
  overkill: number;
}

export interface RoundStat {
  iteration: number;
  create_at: string;
  epoch: number;
  imageCount: number;
  status: ReportTrainRow["status"];
  durationSec: number | null;
  overkill: number | null; // 0~1
  missing: number | null;
  threshold: number | null;
}

export interface ReportMetrics {
  isInspection: boolean; // step "검사": 검증 리스트 기준 집계
  validation: { id: number; finishedAt: string; threshold: number | null; modelName: string; modelUuid: string; modelCreateAt: string } | null;
  aiJudged: number; // AI 판정이 있는 이미지 수
  overkillRate: number | null; // 이미지 기준: 정답 OK 중 AI NG
  missingRate: number | null; // 이미지 기준: 정답 NG 중 AI OK
  projectName: string;
  materialName: string;
  classes: string[];
  total: number;
  labeled: number;
  unlabeled: number;
  ngLabeled: number;
  matchRate: number | null; // 라벨링 이미지 중 AI 일치율
  classStats: ClassStat[];
  confusion: { labels: string[]; matrix: number[][] }; // [정답][AI]
  processStats: ProcessStat[];
  rounds: RoundStat[];
  latestRound: RoundStat | null;
}

// ponytail: DB에 공정 컬럼이 없어 경로에서 추출. /datasets/{제품}/{날짜}/{공정}/{OK|NG}/파일 규칙.
// 공정 정보가 DB에 생기면 이 함수만 교체.
export const getProcess = (path: string) => {
  const parts = path.split("/").filter(Boolean);
  const okNgIdx = parts.findIndex((p) => p === "OK" || p === "NG");
  if (okNgIdx > 0) return parts[okNgIdx - 1];
  return parts.length >= 2 ? parts[parts.length - 2] : "-";
};

const ratio = (a: number, b: number) => (b ? a / b : null);

const parseJson = <T,>(s: string | null | undefined, fallback: T): T => {
  try {
    return s ? JSON.parse(s) : fallback;
  } catch {
    return fallback;
  }
};

const inRange = (date: string, range?: { start_date: string; end_date: string }) => {
  if (!range) return true;
  const d = date.slice(0, 10);
  return (!range.start_date || d >= range.start_date) && (!range.end_date || d <= range.end_date);
};

export function buildReportMetrics(
  raw: ReportRawData,
  options: { dateRange?: { start_date: string; end_date: string }; classFilter?: string[] | null } = {},
): ReportMetrics {
  // OK 는 불량 클래스가 아님 → 클래스 집계에서 제외 (혼동행렬에는 별도 포함)
  const projectClasses: string[] = parseJson<string[]>(raw.project.class, []).filter((c) => c !== OK_LABEL);
  const classes = options.classFilter?.length
    ? projectClasses.filter((c) => options.classFilter!.includes(c))
    : projectClasses;

  // 검사: 선택된 검증 결과의 이미지(검증 리스트)만 사용. 정답 = 사용자 라벨(경로 매칭), AI = score > threshold ? NG : OK
  const isInspection = raw.project.step === "검사";
  const results = parseJson<{ items?: { path: string; label: string; score: number }[]; threshold?: number }>(
    raw.validation?.results,
    {},
  );
  const threshold = results.threshold ?? null;
  let rows = raw.labels;
  if (isInspection) {
    const gtByPath = new Map(raw.labels.map((l) => [l.path, l.label]));
    rows = (results.items ?? []).map((item, i) => ({
      id: i + 1,
      path: item.path,
      label: gtByPath.get(item.path) ?? "",
      AILabel: threshold === null ? item.label : item.score > threshold ? "NG" : OK_LABEL,
      score: item.score,
      recentLabel: "",
      ai_update_at: raw.validation!.finished_at,
    }));
  }

  const labeledRows = rows.filter((l) => l.label);
  const hasOk = labeledRows.some((l) => l.label === OK_LABEL) || rows.some((l) => l.AILabel === OK_LABEL);
  const confusionLabels = hasOk && !classes.includes(OK_LABEL) ? [OK_LABEL, ...classes] : classes;
  const idx = new Map(confusionLabels.map((c, i) => [c, i]));
  const matrix = confusionLabels.map(() => confusionLabels.map(() => 0));

  const classStats: ClassStat[] = classes.map((c) => ({
    className: c,
    labeled: 0,
    predicted: 0,
    correct: 0,
    recall: null,
    precision: null,
    overkill: 0,
    missing: 0,
  }));
  const statOf = new Map(classStats.map((s) => [s.className, s]));
  const processMap = new Map<string, ProcessStat>();

  let okWithAi = 0, okToNg = 0, ngWithAi = 0, ngToOk = 0;
  for (const row of rows) {
    const process = getProcess(row.path);
    let p = processMap.get(process);
    if (!p) {
      p = { process, total: 0, labeled: 0, byClass: {}, mismatch: 0, mismatchRate: null, overkill: 0 };
      processMap.set(process, p);
    }
    p.total++;

    const ai = statOf.get(row.AILabel);
    if (ai) ai.predicted++;
    if (!row.label) continue;

    p.labeled++;
    if (!row.AILabel) continue; // AI 판정 없음 → 일치/오판정 집계 제외
    if (row.label === OK_LABEL) {
      okWithAi++;
      if (row.AILabel !== OK_LABEL) okToNg++;
    } else {
      ngWithAi++;
      if (row.AILabel === OK_LABEL) ngToOk++;
    }
    const gt = statOf.get(row.label);
    if (gt) {
      gt.labeled++;
      p.byClass[row.label] = (p.byClass[row.label] || 0) + 1;
      if (row.AILabel === row.label) gt.correct++;
      if (row.AILabel === OK_LABEL) gt.missing++;
    }
    if (row.AILabel !== row.label) p.mismatch++;
    if (row.label === OK_LABEL && row.AILabel !== OK_LABEL) {
      if (ai) ai.overkill++;
      p.overkill++;
    }
    const gi = idx.get(row.label);
    const ai2 = idx.get(row.AILabel);
    if (gi !== undefined && ai2 !== undefined) matrix[gi][ai2]++;
  }

  for (const s of classStats) {
    s.recall = ratio(s.correct, s.labeled);
    s.precision = ratio(s.correct, s.predicted);
  }
  const processStats = Array.from(processMap.values())
    .map((p) => ({ ...p, mismatchRate: ratio(p.mismatch, p.labeled) }))
    .sort((a, b) => b.total - a.total);

  // 학습 회차: trainHistory + 해당 회차 모델(inspModel.info.performance). 한 회차에 모델이 여러 개면 마지막(최종 epoch) 사용.
  const perfByLog = new Map<string, { overkill: number; missing: number; threshold: number }>();
  for (const m of raw.inspModels) {
    const perf = parseJson<any>(m.info, {})?.performance;
    if (m.trainLog_uuid && perf) perfByLog.set(m.trainLog_uuid, perf);
  }
  const rounds: RoundStat[] = raw.trainHistory
    .filter((t) => inRange(t.create_at, options.dateRange))
    .map((t) => {
      const perf = t.trainLog_uuid ? perfByLog.get(t.trainLog_uuid) : undefined;
      return {
        iteration: t.iteration,
        create_at: t.create_at,
        epoch: t.epoch,
        imageCount: t.imageCount,
        status: t.status,
        durationSec: t.finish_at ? (new Date(t.finish_at).getTime() - new Date(t.create_at).getTime()) / 1000 : null,
        overkill: perf?.overkill ?? null,
        missing: perf?.missing ?? null,
        threshold: perf?.threshold ?? null,
      };
    });
  const finished = rounds.filter((r) => r.status === "Finished");

  const ngLabeled = labeledRows.filter((l) => l.label !== OK_LABEL).length;
  const judged = labeledRows.filter((l) => l.AILabel);
  const model = raw.validation?.model;
  return {
    isInspection,
    validation: raw.validation
      ? {
          id: raw.validation.id,
          finishedAt: raw.validation.finished_at,
          threshold,
          modelName: model?.name ?? "(삭제된 모델)",
          modelUuid: raw.validation.model_uuid ?? "-",
          modelCreateAt: model?.create_at ?? "",
        }
      : null,
    aiJudged: rows.filter((l) => l.AILabel).length,
    overkillRate: ratio(okToNg, okWithAi),
    missingRate: ratio(ngToOk, ngWithAi),
    projectName: raw.project.project_name,
    materialName: raw.project.material_name,
    classes,
    total: rows.length,
    labeled: labeledRows.length,
    unlabeled: rows.length - labeledRows.length,
    ngLabeled,
    matchRate: ratio(judged.filter((l) => l.label === l.AILabel).length, judged.length),
    classStats,
    confusion: { labels: confusionLabels, matrix },
    processStats,
    rounds,
    latestRound: finished[finished.length - 1] ?? null,
  };
}

export const pct = (v: number | null | undefined, digits = 1) =>
  v === null || v === undefined ? "-" : `${(v * 100).toFixed(digits)}%`;
